import api from './api';

/**
 * Read-only EDI Info for a PRO — mirrors the backend GET /orders/{pronumber}/edi
 * (V1 parity port of edi_viewinfo, order branch). Powers the "EDI INFO" panel on
 * the Pro Detail Addl Info tab.
 *
 * Every value is a display-ready string already formatted by the backend (dates,
 * blanked zero-dates, composed multi-line location/contact blocks). Multiple
 * records are returned oldest→newest so the panel can diff them side by side.
 */

/** One EDI order line (V1 edi_viewinfo order-lines sub-table). All display-ready
 *  strings; Master/Secondary are computed backend-side for diageo EDI. */
export interface OrderLine {
  lineNumber: string;
  vendorCode: string;
  batch: string;
  serial: string;
  lbs: string;
  mrPo: string;
  qty: string;
  uom: string;
  masterQty: string;
  masterUom: string;
  secondaryQty: string;
  secondaryUom: string;
  /** No matching product code resolved — V1 flags these with **. */
  missingProduct: boolean;
}

/** One EDI record's header fields plus its order lines. */
export interface EdiRecord {
  /** edi_orders recno of this record — the key for View/Download of its raw EDI file. */
  recno: number;
  /** Blob path of the raw inbound EDI file (blank when there's no file to view/download). */
  nasEdifilename: string;
  /** Transaction type (REPLACE / ADD / REPACK / …) — part of the column header. */
  editype: string;
  /** Formatted process datetime (MM/DD/YYYY HH:mm) — part of the column header. */
  processDatetime: string;

  ediSource: string;
  moveType: string;
  container: string;
  po: string;
  billToRef: string;
  broker: string;
  brokerRef: string;
  /** Contact name over phone (newline-joined). */
  brokerContact: string;
  bol: string;
  booking: string;
  dept: string;
  seal: string;
  pickupNumber: string;
  deliveryApptNumber: string;
  scac: string;
  terminal: string;
  sizeType: string;
  /** edi_equipmentmap recno backing Size/Type — for the "Map Size/Type" action. */
  equipmentMapCode: number;
  /** edi_scacmap recno backing SCAC — for the "Map SCAC" action (0 = none). */
  scacMapCode: number;
  lbsKgs: string;
  priority: string;
  vesselVoyage: string;
  eta: string;
  lfd: string;
  cutoff: string;
  docCutoff: string;
  /** Location name over address (newline-joined) — the EDI-supplied location. */
  pickup: string;
  delivery: string;
  terminate: string;
  /** Mapped CMS location name (blank when unmapped/deleted) + the edi_locations recno
   *  to open the mapping drawer, per slot (delivery/terminate fallback already applied). */
  pickupMapName: string;
  pickupMapCode: number;
  deliveryMapName: string;
  deliveryMapCode: number;
  terminateMapName: string;
  terminateMapCode: number;
  portOfOrigin: string;
  portOfDischarge: string;
  // Raw EDI UN/LOCODE + mapped CMS port name (blank if unmapped) — for the "Map Port" action.
  portOfOriginUnlocode: string;
  portOfOriginMapName: string;
  portOfDischargeUnlocode: string;
  portOfDischargeMapName: string;
  pieceCount: string;
  commodity: string;
  contactInfo: string;
  drop: string;
  hazmat: string;
  rate: string;
  notes: string;
  comments: string;
  additionalInfo: string;
  shipperInfo: string;
  sapOrderNumber: string;
  /** Order lines for this record (empty for most non-warehouse EDIs). */
  orderLines: OrderLine[];
}

/** Fetch all non-deleted EDI records for a PRO (numeric or "T"-template pro). */
async function getProEdi(pronumber: number | string): Promise<EdiRecord[]> {
  const r = await api.get<EdiRecord[]>(`/orders/${pronumber}/edi`);
  return r.data;
}

/** Fetch a single EDI record by its edi_orders recno — same shape as getProEdi
 *  (a one-item list). Used by pending EDI orders, which have no pro yet. */
async function getEdiByRecno(recno: number): Promise<EdiRecord[]> {
  const r = await api.get<EdiRecord[]>(`/orders/edi-record/${recno}`);
  return r.data;
}

const proEdiService = { getProEdi, getEdiByRecno };
export default proEdiService;
